import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider, Drawer, message, Modal, Select, Spin } from 'antd';
import { FileExcelOutlined, FileTextOutlined, SearchOutlined } from '@ant-design/icons';
import { TemplateEditor } from './TemplateEditor';
import { TemplateService, TemplateVersion } from './TemplateService';
import { standardCalcService, StandardCalculation } from '../CalcProfile/StandardCalcService';
import { MODAL_STYLES, MODAL_TITLE_STYLE, DRAWER_STYLES, darkFormTheme } from '../../../styles/modalStyles';
import styles from './Templates.module.css';

// ── Types ──────────────────────────────────────────────────────────────────────

interface TemplateRow {
  fileName: string;
  latestVersion: TemplateVersion;
  allVersions: TemplateVersion[];
}

// ── SC badge colours ───────────────────────────────────────────────────────────

const SC_CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  Tax:          { bg: 'rgba(37,99,235,0.15)',   color: '#60a5fa' },
  Allocation:   { bg: 'rgba(242,101,34,0.15)',  color: '#F26522' },
  Capital:      { bg: 'rgba(124,58,237,0.15)',  color: '#a78bfa' },
  Depreciation: { bg: 'rgba(20,184,166,0.15)',  color: '#2dd4bf' },
  Output:       { bg: 'rgba(34,197,94,0.15)',   color: '#22c55e' },
  Event:        { bg: 'rgba(255,255,255,0.07)', color: '#9ca3af' },
};
const UNTAGGED_COLORS = { bg: 'rgba(255,255,255,0.06)', color: '#6b7280' };

// ── Mock fallback data ─────────────────────────────────────────────────────────

const MOCK_VERSIONS: TemplateVersion[] = [
  {
    id: 3, versionNumber: 3, fileName: 'Template Phase 1.xlsx', blobPath: '',
    fileSizeBytes: 250880, uploadedBy: 'admin',
    uploadedAt: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
    isActive: true, notes: 'Fixed INDEX/MATCH on BP IDs',
    standardCalculationCode: 'SC-001', standardCalculationName: 'Ordinary Income Allocation',
  },
  {
    id: 2, versionNumber: 2, fileName: 'Template Phase 1.xlsx', blobPath: '',
    fileSizeBytes: 244736, uploadedBy: 'admin',
    uploadedAt: new Date(Date.now() - 5 * 24 * 3600 * 1000).toISOString(),
    isActive: false, notes: 'Added StateWH and Summary sheets',
    standardCalculationCode: 'SC-001', standardCalculationName: 'Ordinary Income Allocation',
  },
  {
    id: 1, versionNumber: 1, fileName: 'Template Phase 1.xlsx', blobPath: '',
    fileSizeBytes: 210944, uploadedBy: 'admin',
    uploadedAt: new Date(Date.now() - 12 * 24 * 3600 * 1000).toISOString(),
    isActive: false, notes: 'Initial proof-of-concept template',
    standardCalculationCode: 'SC-001', standardCalculationName: 'Ordinary Income Allocation',
  },
];

const MOCK_SCS: StandardCalculation[] = [
  { id: 1, code: 'SC-001', name: 'Ordinary Income Allocation', category: 'Tax',        description: '', periodType: 'Operational', dependencies: '', isActive: true, sortOrder: 1 },
  { id: 2, code: 'SC-002', name: 'Capital Account Rollforward', category: 'Capital',   description: '', periodType: 'Operational', dependencies: '', isActive: true, sortOrder: 2 },
  { id: 3, code: 'SC-003', name: 'Depreciation Schedule',       category: 'Depreciation', description: '', periodType: 'Operational', dependencies: '', isActive: true, sortOrder: 3 },
  { id: 4, code: 'SC-004', name: 'K-1 Output Generation',       category: 'Output',    description: '', periodType: 'Operational', dependencies: '', isActive: true, sortOrder: 4 },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

function groupVersions(versions: TemplateVersion[]): TemplateRow[] {
  const map = new Map<string, TemplateVersion[]>();
  for (const v of versions) {
    if (!map.has(v.fileName)) map.set(v.fileName, []);
    map.get(v.fileName)!.push(v);
  }
  return Array.from(map.entries()).map(([fileName, vers]) => {
    const sorted = [...vers].sort((a, b) => b.versionNumber - a.versionNumber);
    return { fileName, latestVersion: sorted[0], allVersions: sorted };
  });
}

// ── SC Badge ───────────────────────────────────────────────────────────────────

function ScBadge({
  code,
  name,
  scMap,
}: {
  code?: string;
  name?: string;
  scMap: Map<string, StandardCalculation>;
}) {
  if (!code) {
    const { bg, color } = UNTAGGED_COLORS;
    return <span className={styles.scBadge} style={{ background: bg, color }}>Untagged</span>;
  }
  const sc = scMap.get(code);
  const { bg, color } = sc ? (SC_CATEGORY_COLORS[sc.category] ?? UNTAGGED_COLORS) : UNTAGGED_COLORS;
  return <span className={styles.scBadge} style={{ background: bg, color }}>{name ?? code}</span>;
}

// ── View Structure Modal ───────────────────────────────────────────────────────

type StructTab = 'sheets' | 'ranges' | 'mapping';

const MOCK_SHEETS = [
  { name: 'Inputs',    purpose: 'Partner data & trial balance',      type: 'Input'  },
  { name: 'K1_Alloc',  purpose: 'Income allocation engine',           type: 'Calc'   },
  { name: 'K1_Output', purpose: 'K-1 line item outputs',              type: 'Output' },
  { name: 'StateWH',   purpose: 'State withholding calculation',      type: 'Calc'   },
  { name: 'Summary',   purpose: 'Fund-level summary',                 type: 'Output' },
];

const MOCK_RANGES = [
  { name: 'PARTNER_COUNT',   ref: 'Inputs!A1'   },
  { name: 'TAX_YEAR',        ref: 'Inputs!B1'   },
  { name: 'ORDINARY_INCOME', ref: 'K1_Alloc!C5' },
  { name: 'NET_RENTAL',      ref: 'K1_Alloc!C6' },
  { name: 'INTEREST_INC',    ref: 'K1_Alloc!C7' },
  { name: 'CAPITAL_GAIN',    ref: 'K1_Alloc!C8' },
  { name: 'ENTITY_NAME',     ref: 'Inputs!A2'   },
  { name: 'ENTITY_EIN',      ref: 'Inputs!B2'   },
];

const MOCK_MAPPING = [
  { k1Line: 'K1_L1',  desc: 'Ordinary business income',    ref: 'K1_Output!B2:B{n}' },
  { k1Line: 'K1_L2',  desc: 'Net rental real estate inc.', ref: 'K1_Output!C2:C{n}' },
  { k1Line: 'K1_L5',  desc: 'Interest income',             ref: 'K1_Output!D2:D{n}' },
  { k1Line: 'K1_L6a', desc: 'Ordinary dividends',          ref: 'K1_Output!E2:E{n}' },
  { k1Line: 'K1_L9',  desc: 'Net long-term capital gain',  ref: 'K1_Output!F2:F{n}' },
  { k1Line: 'K1_L14', desc: 'Self-employment earnings',    ref: 'K1_Output!G2:G{n}' },
];

function SheetTypeBadge({ type }: { type: string }) {
  if (type === 'Calc')  return <span className={styles.sheetTypeCalc}>Calc</span>;
  if (type === 'Input') return <span className={styles.sheetTypeInput}>Input</span>;
  return <span className={styles.sheetTypeOutput}>Output</span>;
}

const ViewStructureModal: React.FC<{
  fileName: string;
  onClose: () => void;
}> = ({ fileName, onClose }) => {
  const [tab, setTab] = useState<StructTab>('sheets');

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>{`Template Structure — ${fileName}`}</span>}
      centered
      width={640}
      styles={MODAL_STYLES}
    >
      <p className={styles.structNote}>Structure preview — based on registered mappings</p>

      <div className={styles.modalTabRow}>
        {(['sheets', 'ranges', 'mapping'] as StructTab[]).map(t => (
          <button
            key={t}
            className={tab === t ? styles.modalTabActive : styles.modalTab}
            onClick={() => setTab(t)}
          >
            {t === 'sheets' ? 'Sheets' : t === 'ranges' ? 'Named Ranges' : 'Input / Output Mapping'}
          </button>
        ))}
      </div>

      {tab === 'sheets' && (
        <table className={styles.structTable}>
          <thead><tr><th>Sheet</th><th>Purpose</th><th>Type</th></tr></thead>
          <tbody>
            {MOCK_SHEETS.map(s => (
              <tr key={s.name}>
                <td className={styles.codeCell}>{s.name}</td>
                <td style={{ color: 'var(--ce-text-secondary)', fontSize: 13 }}>{s.purpose}</td>
                <td><SheetTypeBadge type={s.type} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'ranges' && (
        <table className={styles.structTable}>
          <thead><tr><th>Named Range</th><th>Cell Reference</th></tr></thead>
          <tbody>
            {MOCK_RANGES.map(r => (
              <tr key={r.name}>
                <td className={styles.codeCell}>{r.name}</td>
                <td className={styles.codeCell} style={{ color: '#22c55e' }}>{r.ref}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'mapping' && (
        <table className={styles.structTable}>
          <thead><tr><th>K-1 Line</th><th>Description</th><th>Excel Range</th></tr></thead>
          <tbody>
            {MOCK_MAPPING.map(m => (
              <tr key={m.k1Line}>
                <td className={styles.codeCell}>{m.k1Line}</td>
                <td style={{ color: 'var(--ce-text-secondary)', fontSize: 13 }}>{m.desc}</td>
                <td className={styles.codeCell} style={{ color: '#60a5fa' }}>{m.ref}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
};

// ── Versions Drawer ────────────────────────────────────────────────────────────

const VersionsDrawer: React.FC<{
  template: TemplateRow | null;
  onClose: () => void;
  onActivated: () => void;
}> = ({ template, onClose, onActivated }) => {
  const handleActivate = async (v: TemplateVersion) => {
    try {
      await TemplateService.activate(v.id);
      onActivated();
      message.success(`v${v.versionNumber} set as active`);
    } catch {
      message.error('Failed to activate version');
    }
  };

  const handleDownload = (v: TemplateVersion) => {
    TemplateService.download(v.id, v.fileName).catch(() => message.error('Download failed'));
  };

  return (
    <Drawer
      open={!!template}
      onClose={onClose}
      width={580}
      title={
        <span style={{ color: '#fff', fontSize: 14 }}>
          Version History
          {template && (
            <span style={{ marginLeft: 10, fontSize: 11, color: 'rgba(255,255,255,0.40)' }}>
              {template.fileName}
            </span>
          )}
        </span>
      }
      styles={DRAWER_STYLES}
    >
      {template && (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Version</th>
                <th>Uploaded By</th>
                <th>Date</th>
                <th>Size</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {template.allVersions.map(v => (
                <tr key={v.id} className={styles.tableRow}>
                  <td><span className={styles.versionBadge}>v{v.versionNumber}</span></td>
                  <td style={{ color: 'var(--ce-text-secondary)', fontSize: 13 }}>{v.uploadedBy ?? 'admin'}</td>
                  <td style={{ color: 'var(--ce-text-secondary)', fontSize: 13 }}>{formatRelative(v.uploadedAt)}</td>
                  <td style={{ color: 'var(--ce-text-secondary)', fontSize: 13, fontFamily: 'monospace' }}>{formatBytes(v.fileSizeBytes)}</td>
                  <td>
                    {v.isActive
                      ? <span className={styles.badgeActive}>Active</span>
                      : <span className={styles.badgeInactive}>Inactive</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className={`${styles.btnOutline} ${styles.btnSm}`}
                        onClick={() => handleDownload(v)}
                      >
                        Download
                      </button>
                      {!v.isActive && (
                        <button
                          className={`${styles.btnOutline} ${styles.btnSm}`}
                          onClick={() => handleActivate(v)}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {template.allVersions[0]?.notes && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ce-text-muted)', fontStyle: 'italic' }}>
              Latest: {template.allVersions[0].notes}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

// ── Tag SC Modal ───────────────────────────────────────────────────────────────

const TagScModal: React.FC<{
  template: TemplateRow;
  scList: StandardCalculation[];
  onClose: () => void;
  onTagged: () => void;
}> = ({ template, scList, onClose, onTagged }) => {
  const [scCode, setScCode] = useState<string | undefined>(
    template.latestVersion.standardCalculationCode ?? undefined,
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!scCode) return;
    setSaving(true);
    try {
      await TemplateService.reassignSc(template.latestVersion.id, scCode);
      message.success('Standard Calculation assigned');
      onTagged();
    } catch (err: unknown) {
      const status = err instanceof Error ? err.message : '';
      if (status.includes('404') || status.includes('501') || status.includes('405')) {
        message.warning('BackEnd endpoint not yet implemented — SC tagging requires PATCH /api/template/{id}/sc');
      } else {
        message.error('Failed to assign Standard Calculation');
      }
    } finally {
      setSaving(false);
    }
  };

  const scOptions = scList.map(sc => ({ value: sc.code, label: `${sc.code} — ${sc.name}` }));

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>Assign Standard Calculation</span>}
      centered
      width={480}
      styles={MODAL_STYLES}
    >
      <div className={styles.formStack}>
        <div style={{ fontSize: 13, color: 'var(--ce-text-secondary)', marginBottom: 4 }}>
          Template: <span style={{ color: 'var(--ce-text-primary)', fontWeight: 500 }}>{template.fileName}</span>
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Standard Calculation</label>
          <ConfigProvider theme={darkFormTheme}>
            <Select
              style={{ width: '100%' }}
              placeholder="Select a standard calculation…"
              options={scOptions}
              value={scCode}
              onChange={(val: string) => setScCode(val)}
              showSearch
              optionFilterProp="label"
              getPopupContainer={node => node.parentElement ?? document.body}
              allowClear
              onClear={() => setScCode(undefined)}
            />
          </ConfigProvider>
        </div>
      </div>
      <div className={styles.formActions}>
        <button className={styles.btnOutline} onClick={onClose} disabled={saving}>Cancel</button>
        <button className={styles.btnPrimary} onClick={handleSave} disabled={!scCode || saving}>
          {saving ? 'Saving…' : 'Assign'}
        </button>
      </div>
    </Modal>
  );
};

// ── Upload Modal ───────────────────────────────────────────────────────────────

const UploadModal: React.FC<{
  scList: StandardCalculation[];
  onClose: () => void;
  onUploaded: () => void;
}> = ({ scList, onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [scCode, setScCode] = useState<string | undefined>(undefined);
  const [scError, setScError] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<TemplateVersion | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = (f: File) => {
    const lower = f.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      message.error('Only .xlsx and .xlsm files are accepted');
      return;
    }
    if (f.size > 50 * 1024 * 1024) { message.error('File exceeds 50 MB limit'); return; }
    setFile(f);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!scCode) { setScError(true); return; }
    setScError(false);
    setUploading(true);
    try {
      const result = await TemplateService.upload(file, undefined, notes || undefined, scCode);
      setUploadResult(result);
      if (result.isValid === false) {
        message.error('Template uploaded but failed structure validation');
      } else {
        message.success(`${file.name} uploaded successfully`);
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Upload failed';
      message.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const scOptions = scList.map(sc => ({
    value: sc.code,
    label: `${sc.code} — ${sc.name}`,
  }));

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>Upload New Template</span>}
      centered
      width={520}
      styles={MODAL_STYLES}
    >
      <div className={styles.formStack}>
        <div
          className={`${styles.dropZone}${dragOver ? ' ' + styles.dropZoneActive : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files[0];
            if (f) accept(f);
          }}
          onClick={() => inputRef.current?.click()}
        >
          <FileExcelOutlined style={{ fontSize: 28, color: '#F26522' }} />
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ce-text-primary)', margin: '8px 0 4px' }}>
            {file ? file.name : 'Drag & drop your Excel template here, or click to browse'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ce-text-muted)' }}>
            {file ? formatBytes(file.size) : 'Only .xlsx and .xlsm files accepted (max 50MB)'}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xlsm"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) accept(f); }}
          />
        </div>

        {file && !uploadResult && (
          <div className={styles.dropZoneFile}>✓ {file.name} ready to upload</div>
        )}

        {uploadResult && (
          <div style={{
            padding: 10, borderRadius: 6,
            background: uploadResult.isValid === false ? 'rgba(239,68,68,0.10)' : 'rgba(34,197,94,0.10)',
            border: `1px solid ${uploadResult.isValid === false ? 'rgba(239,68,68,0.30)' : 'rgba(34,197,94,0.30)'}`,
            fontSize: 12,
          }}>
            {uploadResult.isValid === false ? (
              <div style={{ color: '#ef4444' }}>✗ Invalid template structure — cannot be assigned to a calc profile item</div>
            ) : (uploadResult.sheetsDetected?.length ?? 0) > 0 ? (
              <>
                <div style={{ color: '#22c55e', marginBottom: 4 }}>
                  ✅ {uploadResult.sheetsDetected!.length} sheet{uploadResult.sheetsDetected!.length !== 1 ? 's' : ''} detected:
                </div>
                <div style={{ color: 'var(--ce-text-secondary)' }}>
                  {uploadResult.sheetsDetected!.map(s => `${s.sheetName} (${s.role})`).join(', ')}
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--ce-text-secondary)' }}>
                Legacy-format template — no new-convention sheets detected.
              </div>
            )}
          </div>
        )}

        <div className={styles.formRow}>
          <label className={styles.formLabel}>
            Standard Calculation <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <ConfigProvider theme={darkFormTheme}>
            <Select
              style={{ width: '100%' }}
              placeholder="Select a standard calculation…"
              options={scOptions}
              value={scCode}
              onChange={(val: string) => { setScCode(val); setScError(false); }}
              showSearch
              optionFilterProp="label"
              status={scError ? 'error' : undefined}
              getPopupContainer={node => node.parentElement ?? document.body}
            />
          </ConfigProvider>
          {scError && (
            <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
              Please select a standard calculation
            </span>
          )}
        </div>

        <div className={styles.formRow}>
          <label className={styles.formLabel}>Version Notes (optional)</label>
          <textarea
            className={styles.formTextarea}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Version notes or changelog"
          />
        </div>
      </div>

      <div className={styles.formActions}>
        {uploadResult ? (
          <button className={styles.btnPrimary} onClick={onUploaded}>Done</button>
        ) : (
          <>
            <button className={styles.btnOutline} onClick={onClose} disabled={uploading}>Cancel</button>
            <button className={styles.btnPrimary} onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? 'Uploading…' : 'Upload Template'}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

// ── Templates (main) ───────────────────────────────────────────────────────────

export const Templates: React.FC = () => {
  const [versions, setVersions]           = useState<TemplateVersion[]>([]);
  const [scList, setScList]               = useState<StandardCalculation[]>([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [scFilter, setScFilter]           = useState('');
  const [showUpload, setShowUpload]       = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const [structureFile, setStructureFile] = useState('');
  const [editorOpen, setEditorOpen]       = useState(false);
  const [editorId, setEditorId]           = useState<number | null>(null);
  const [editorFile, setEditorFile]       = useState('');
  const [versionsTemplate, setVersionsTemplate] = useState<TemplateRow | null>(null);
  const [tagTemplate, setTagTemplate]           = useState<TemplateRow | null>(null);

  const scMap = useMemo(() => {
    const m = new Map<string, StandardCalculation>();
    for (const sc of scList) m.set(sc.code, sc);
    return m;
  }, [scList]);

  const loadTemplates = useCallback(() => {
    setLoading(true);
    TemplateService.getVersions()
      .then(setVersions)
      .catch(() => setVersions(MOCK_VERSIONS))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTemplates();
    standardCalcService.getAll()
      .then(setScList)
      .catch(() => setScList(MOCK_SCS));
  }, [loadTemplates]);

  const rows = useMemo(() => {
    const src = versions.length > 0 ? versions : MOCK_VERSIONS;
    return groupVersions(src);
  }, [versions]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(r => r.fileName.toLowerCase().includes(q));
    }
    if (scFilter) {
      result = result.filter(r => r.latestVersion.standardCalculationCode === scFilter);
    }
    return result;
  }, [rows, search, scFilter]);

  const displaySCs = scList.length > 0 ? scList : MOCK_SCS;

  const handleDownload = (v: TemplateVersion) => {
    TemplateService.download(v.id, v.fileName).catch(() => message.error('Download failed'));
  };

  const openEditor = (row: TemplateRow) => {
    setEditorId(row.latestVersion.id);
    setEditorFile(row.fileName);
    setEditorOpen(true);
  };

  return (
    <div className={styles.pageWrap}>
      {/* Header */}
      <div className={styles.pageHead}>
        <div className={styles.titleBlock}>
          <h1 className={styles.pageTitle}>Template Management</h1>
          <p className={styles.pageSub}>Upload, version, and assign Excel calculation templates</p>
        </div>
        <div className={styles.headActions}>
          <button className={styles.btnPrimary} onClick={() => setShowUpload(true)}>
            + Upload Template
          </button>
        </div>
      </div>

      <div className={styles.scrollContent}>
        {/* Filter bar */}
        <div className={styles.filterBar}>
          <div className={styles.searchWrap}>
            <SearchOutlined className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search templates…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className={styles.filterSelect}
            value={scFilter}
            onChange={e => setScFilter(e.target.value)}
          >
            <option value="">All Standard Calculations</option>
            {displaySCs.map(sc => (
              <option key={sc.code} value={sc.code}>{sc.name}</option>
            ))}
          </select>
        </div>

        {/* Template library */}
        {loading ? (
          <div className={styles.loadingState}><Spin /></div>
        ) : filteredRows.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><FileTextOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
            <div className={styles.emptyTitle}>No templates found</div>
            <div className={styles.emptyText}>
              {search || scFilter
                ? 'Try clearing the filters.'
                : 'Click "+ Upload Template" to add the first template.'}
            </div>
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Template Name</th>
                  <th>Standard Calculation</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Uploaded</th>
                  <th>Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => {
                  const v = row.latestVersion;
                  return (
                    <tr key={row.fileName} className={styles.tableRow}>
                      <td>
                        <div className={styles.cellName} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FileExcelOutlined style={{ color: '#22c55e', fontSize: 14, flexShrink: 0 }} />
                          {row.fileName}
                        </div>
                      </td>
                      <td>
                        <ScBadge
                          code={v.standardCalculationCode}
                          name={v.standardCalculationName}
                          scMap={scMap}
                        />
                      </td>
                      <td>
                        <span className={styles.versionBadge}>v{v.versionNumber}</span>
                      </td>
                      <td>
                        {v.isActive
                          ? <span className={styles.badgeActive}>Active</span>
                          : <span className={styles.badgeInactive}>Inactive</span>
                        }
                      </td>
                      <td style={{ color: 'var(--ce-text-secondary)', fontSize: 13 }}>
                        {formatRelative(v.uploadedAt)}
                      </td>
                      <td style={{ color: 'var(--ce-text-secondary)', fontSize: 13, fontFamily: 'monospace' }}>
                        {formatBytes(v.fileSizeBytes)}
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          <button
                            className={`${styles.btnOutline} ${styles.btnSm}`}
                            onClick={() => handleDownload(v)}
                          >
                            Download
                          </button>
                          <button
                            className={`${styles.btnOutline} ${styles.btnSm}`}
                            onClick={() => { setStructureFile(row.fileName); setShowStructure(true); }}
                          >
                            View Structure
                          </button>
                          <button
                            className={`${styles.btnOutline} ${styles.btnSm}`}
                            style={{ color: '#F26522', borderColor: 'rgba(242,101,34,0.35)' }}
                            onClick={() => openEditor(row)}
                          >
                            Open Editor
                          </button>
                          <button
                            className={`${styles.btnOutline} ${styles.btnSm}`}
                            onClick={() => setVersionsTemplate(row)}
                          >
                            Versions ({row.allVersions.length})
                          </button>
                          <button
                            className={`${styles.btnOutline} ${styles.btnSm}`}
                            style={{ color: '#60a5fa', borderColor: 'rgba(96,165,250,0.35)' }}
                            onClick={() => setTagTemplate(row)}
                          >
                            Tag SC
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className={styles.tableFooter}>
              {filteredRows.length} template{filteredRows.length !== 1 ? 's' : ''}
              {(search || scFilter) && rows.length !== filteredRows.length
                ? ` (filtered from ${rows.length})`
                : ''}
            </div>
          </>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal
          scList={displaySCs}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); loadTemplates(); }}
        />
      )}

      {/* View Structure Modal */}
      {showStructure && (
        <ViewStructureModal
          fileName={structureFile}
          onClose={() => setShowStructure(false)}
        />
      )}

      {/* Editor Drawer */}
      <Drawer
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        width="92vw"
        title={
          <span style={{ color: '#fff', fontSize: 14 }}>
            Template Editor
            {editorFile && (
              <span style={{ marginLeft: 12, fontSize: 11, color: 'rgba(255,255,255,0.40)' }}>
                {editorFile}
              </span>
            )}
          </span>
        }
        styles={{ ...DRAWER_STYLES, body: { ...DRAWER_STYLES.body, padding: 0, height: '100%' } }}
      >
        {editorId && (
          <TemplateEditor
            templateId={editorId}
            fileName={editorFile}
            onClose={() => setEditorOpen(false)}
            onSaved={() => { setEditorOpen(false); loadTemplates(); }}
          />
        )}
      </Drawer>

      {/* Versions Drawer */}
      <VersionsDrawer
        template={versionsTemplate}
        onClose={() => setVersionsTemplate(null)}
        onActivated={() => { loadTemplates(); setVersionsTemplate(null); }}
      />

      {/* Tag SC Modal */}
      {tagTemplate && (
        <TagScModal
          template={tagTemplate}
          scList={displaySCs}
          onClose={() => setTagTemplate(null)}
          onTagged={() => { setTagTemplate(null); loadTemplates(); }}
        />
      )}
    </div>
  );
};
